/**
 * Mum grafigi + UT Bot trailing stop + SR kanallari.
 *
 * lightweight-charts kullanilir (TradingView'in acik kaynak kutuphanesi).
 * Cizilen seriler DOGRUDAN backend'in Indicator Mirror ciktisidir; UI
 * kendi indikator hesabini YAPMAZ - aksi halde ekranda gorunen ile
 * karar veren mantik ayrisirdi.
 */

import { useEffect, useRef, useState } from "react";
import {
  ColorType,
  createChart,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import type { Candles } from "../api/types";

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function TradingChart({
  data,
  theme,
  height = 300,
}: {
  data: Candles | null;
  theme: "light" | "dark";
  height?: number;
}) {
  const container = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const candleSeries = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const stopSeries = useRef<ISeriesApi<"Line"> | null>(null);
  const volumeSeries = useRef<ISeriesApi<"Histogram"> | null>(null);
  // SR kanal cizgileri - her veri guncellemesinde temizlenip yeniden kurulur.
  const priceLines = useRef<IPriceLine[]>([]);
  // Tema degisince grafik SIFIRDAN kurulur. Bu sayac, veri efektinin de
  // yeniden calismasini saglar; aksi halde yeni grafik BOS kalirdi.
  const [revision, setRevision] = useState(0);

  // Grafik kurulumu - tema degisince yeniden kurulur (renkler token'lardan gelir).
  useEffect(() => {
    if (!container.current) return;

    const instance = createChart(container.current, {
      height,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: cssVar("--text-2"),
        fontFamily: cssVar("--sans") || "sans-serif",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: cssVar("--border") },
        horzLines: { color: cssVar("--border") },
      },
      rightPriceScale: { borderColor: cssVar("--border") },
      timeScale: { borderColor: cssVar("--border"), timeVisible: true },
      crosshair: { mode: 0 },
      handleScale: { axisPressedMouseMove: { time: true, price: false } },
    });

    const up = cssVar("--up");
    const down = cssVar("--down");

    candleSeries.current = instance.addCandlestickSeries({
      upColor: up,
      downColor: down,
      borderUpColor: up,
      borderDownColor: down,
      wickUpColor: up,
      wickDownColor: down,
    });

    stopSeries.current = instance.addLineSeries({
      color: cssVar("--warn"),
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      title: "UT Stop",
    });

    volumeSeries.current = instance.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
      color: cssVar("--neutral"),
    });
    instance.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });

    chart.current = instance;
    priceLines.current = [];
    setRevision((value) => value + 1);

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) instance.applyOptions({ width });
    });
    observer.observe(container.current);

    return () => {
      observer.disconnect();
      instance.remove();
      chart.current = null;
      candleSeries.current = null;
      stopSeries.current = null;
      volumeSeries.current = null;
    };
  }, [theme, height]);

  // Veri guncelleme
  useEffect(() => {
    if (!data || !candleSeries.current || !stopSeries.current || !volumeSeries.current) {
      return;
    }

    const up = cssVar("--up");
    const down = cssVar("--down");

    candleSeries.current.setData(
      data.candles.map((candle) => ({
        time: candle.time as UTCTimestamp,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      })),
    );

    volumeSeries.current.setData(
      data.candles.map((candle) => ({
        time: candle.time as UTCTimestamp,
        value: candle.volume,
        color: candle.close >= candle.open ? `${up}44` : `${down}44`,
      })),
    );

    // Warmup boyunca stop `null`'dur; bu noktalar cizilmez (uydurulmaz).
    stopSeries.current.setData(
      data.candles
        .map((candle, index) => ({
          time: candle.time as UTCTimestamp,
          value: data.ut_trailing_stop[index],
        }))
        .filter((point): point is { time: UTCTimestamp; value: number } =>
          point.value != null && Number.isFinite(point.value),
        ),
    );

    // UT Bot yukselis/dusus isaretleri - metin YOK (kullanici istegi), ok
    // sekli + renk yonu tek basina yeterince acik.
    candleSeries.current.setMarkers(
      data.ut_signals.map((signal) => ({
        time: signal.time as Time,
        position: signal.type === "BUY" ? ("belowBar" as const) : ("aboveBar" as const),
        color: signal.type === "BUY" ? up : down,
        shape: signal.type === "BUY" ? ("arrowUp" as const) : ("arrowDown" as const),
        text: "",
      })),
    );

    // SR kanallari yatay cizgi olarak
    const series = candleSeries.current;
    for (const line of priceLines.current) {
      series.removePriceLine(line);
    }
    priceLines.current = [];

    for (const level of data.levels.slice(0, 6)) {
      const isResistance = level.kind === "RESISTANCE";
      priceLines.current.push(
        series.createPriceLine({
          price: level.top,
          color: isResistance ? down : level.kind === "SUPPORT" ? up : cssVar("--neutral"),
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: isResistance ? "Direnç" : level.kind === "SUPPORT" ? "Destek" : "Kanal",
        }),
      );
    }

    chart.current?.timeScale().fitContent();
  }, [data, revision]);

  return (
    <div style={{ position: "relative" }}>
      <div ref={container} style={{ width: "100%" }} />
      {!data && (
        <div
          className="muted center"
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          Grafik yükleniyor…
        </div>
      )}
    </div>
  );
}
